import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

import { Router, RouterLink } from '@angular/router';

import { PaymentService } from '../../shared/services/payment.service';
import { environment } from '../../../../environments/environment';
import { first } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Subscription, firstValueFrom } from 'rxjs';


@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HttpClientModule,
    RouterLink
  ],
  providers: [
    PaymentService
  ]
})
export class RegistrationComponent implements OnInit, OnDestroy {
  step = 1;
  step1!: FormGroup;
  step2!: FormGroup;
  step3!: FormGroup;
  step4!: FormGroup;
  isLoading = false;
  registrationSuccess = false;
  errorMessage = '';
  tenantNameChecking = false;
  tenantNameAvailable = false;
  tenantNameChecked = false;
  tenantNameError = '';

  // Email availability check properties
  emailChecking = false;
  emailAvailable = false;
  emailChecked = false;
  emailError = '';

  // Payment related properties
  @ViewChild('paymentElement') paymentElementRef?: ElementRef;

  // Paystack payment related properties
  paystackPaymentRef: string | null = null;
  paystackEmail: string = '';
  paystackAmount: number = 0;
  paystackPlans: any[] = [];
  paymentProcessing = false;
  paymentError: string | null = null;

  // Paystack payment related properties
  paystackCustomerCode: string = '';
  paymentMethod: 'paystack' = 'paystack';
  paystackPublicKey: string = (environment as any).paystackPublicKey || '';

  // Plan selection properties
  plans: any[] = [];
  selectedPlan: any = null;
  loadingPlans = false;

  // Trial mode
  isTrialMode = false;
  trialTokenizationAmount = 100; // R1.00 in cents

  // Define the admin user fields required by the create-database endpoint
  profileFields = [
    { key: 'firstName', label: 'First Name', placeholder: 'Your first name' },
    { key: 'lastName', label: 'Last Name', placeholder: 'Your last name' },
    { key: 'adminEmail', label: 'Email Address', placeholder: 'Your email address' }
  ];

  constructor(
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  ngOnInit() {
    // Load both Paystack plans
    this.loadPlans();

    // Step 1: Tenant name
    this.step1 = this.fb.group({
      tenantName: ['', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
        Validators.pattern(/^[a-zA-Z0-9_\-\s]+$/)
      ]]
    });

    // Listen for changes in tenant name to check availability
    this.step1.get('tenantName')?.valueChanges.subscribe(value => {
      if (value && value.length >= 3) {
        this.checkTenantNameAvailability(value);
      } else {
        this.tenantNameChecked = false;
        this.tenantNameAvailable = false;
        this.tenantNameError = '';
      }
    });

    // Step 2: Admin user details
    const group: any = {};
    this.profileFields.forEach(f => {
      if (f.key === 'adminEmail') {
        group[f.key] = ['', [Validators.required, Validators.email]];
      } else {
        group[f.key] = ['', [Validators.required, Validators.maxLength(50)]];
      }
    });
    this.step2 = this.fb.group(group);

    // Listen for changes in admin email to check availability
    this.step2.get('adminEmail')?.valueChanges.subscribe(value => {
      if (value && this.step2.get('adminEmail')?.valid) {
        this.checkEmailAvailability(value);
      } else {
        this.emailChecked = false;
        this.emailAvailable = false;
        this.emailError = '';
      }
    });

    // Step 3: T&C acceptance
    this.step3 = this.fb.group({
      accepted: [false, Validators.requiredTrue]
    });

    // Step 4: Payment
    this.step4 = this.fb.group({});
  }

  async loadPlans(retryCount = 0): Promise<void> {
    const maxRetries = 2;
    this.loadingPlans = true;
    this.plans = [];
    this.errorMessage = '';
    const apiUrl = `${environment.apiUrl}/api/Subscription/paystack/plans`;

// $&

    try {
      // Add timestamp to prevent caching
      const url = `${apiUrl}`;

// $&

      // Add headers to prevent caching and specify we want JSON
      const response = await firstValueFrom(
        this.http.get(url, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Accept': 'application/json'
          },
          observe: 'response'
        })
      ) as any;

// $&
// $&
// $&

      const responseData = response.body;

      if (!responseData) {
        throw new Error('Empty response from server');
      }

      if (responseData.status === true && Array.isArray(responseData.data)) {
        // Only include plans that have a valid Paystack plan_code (PLN_...) and are not deleted.
        // To retire a plan so it stops appearing here, archive or deactivate it in Paystack —
        // the backend already filters by status=active, so Paystack is the source of truth.
        const activePlans = responseData.data.filter((plan: any) => {
          const isPlanDeleted = plan.is_deleted === true ||
                              plan.is_deleted === 'true' ||
                              plan.is_deleted === 1 ||
                              plan.is_deleted === '1' ||
                              plan.status === 'deleted' ||
                              plan.status === 'archived';

          if (isPlanDeleted) {
            return false;
          }

          return true;
        });

        // Then filter for valid plan codes and map to the required format
        const validPlans = activePlans
          .filter((plan: any) => {
            const hasValidPlanCode = plan.plan_code &&
                                  typeof plan.plan_code === 'string' &&
                                  plan.plan_code.startsWith('PLN_');

            if (!hasValidPlanCode) {
            }

            return hasValidPlanCode;
          })
          .map((plan: any) => ({
            id: plan.plan_code,
            paystack_plan_code: plan.plan_code,
            name: plan.name || 'Unnamed Plan',
            description: plan.description || 'No description available',
            amount: (plan.amount || 0) / 100, // Convert from kobo to currency
            currency: plan.currency || 'USD',
            interval: plan.interval || 'monthly',
            intervalCount: 1,
            isPopular: false,
            features: [
              plan.description || 'Standard features included',
              '24/7 Support',
              'Secure Payments'
            ]
          }));
        this.paystackPlans = validPlans;

// $&
      } else {
// $&
        throw new Error(responseData.message || 'Invalid response format from server');
      }
    } catch (error: any) {
      // console.error('Error loading Paystack plans:', {
      //   errorObject: error,
      //   name: error.name,
      //   message: error.message,
      //   status: error.status,
      //   statusText: error.statusText,
      //   url: error.url,
      //   headers: error.headers,
      //   errorDetails: error.error
      // });

      // If we have retries left and this might be a temporary issue, retry
      if (retryCount < maxRetries) {
        const delay = 1000 * (retryCount + 1);
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(this.loadPlans(retryCount + 1));
          }, delay);
        });
      }

      // If we've exhausted retries, show a user-friendly error
      this.errorMessage = 'Failed to load subscription plans. Please refresh the page or try again later.';

      if (error.status === 0) {
        this.errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      } else if (error.status === 401 || error.status === 403) {
        this.errorMessage = 'Authentication error. Please ensure you have the correct permissions.';
      } else if (error.error?.message) {
        this.errorMessage = error.error.message;
      } else if (error.message) {
        this.errorMessage = error.message;
      }

// $&
      this.paystackPlans = [];
    } finally {
      this.loadingPlans = false;
      this.cdr.detectChanges();
    }
  }

  checkTenantNameAvailability(tenantName: string) {
    if (!tenantName) return;

    this.tenantNameChecking = true;
    this.tenantNameChecked = false;
    this.tenantNameError = '';

    this.http.post(`${environment.apiUrl}/api/Tenant/check-name`, { tenantName })
      .subscribe(
        (response: any) => {
          this.tenantNameChecking = false;
          this.tenantNameChecked = true;

          if (response && response.isAvailable) {
            this.tenantNameAvailable = true;
            this.tenantNameError = '';
          } else {
            this.tenantNameAvailable = false;
            this.tenantNameError = response.message || 'This name is already taken. Please choose a different name.';
          }
        },
        (error) => {
          this.tenantNameChecking = false;
          this.tenantNameChecked = true;
          this.tenantNameAvailable = false;
          this.tenantNameError = 'Error checking name availability. Please try again.';
// $&
        }
      );
  }

  checkEmailAvailability(email: string) {
    if (!email) return;

    this.emailChecking = true;
    this.emailChecked = false;
    this.emailError = '';

    this.http.post(`${environment.apiUrl}/api/Tenant/check-admin-email`, { email })
      .subscribe(
        (response: any) => {
          this.emailChecking = false;
          this.emailChecked = true;

          if (response && response.isAvailable) {
            this.emailAvailable = true;
            this.emailError = '';
          } else {
            this.emailAvailable = false;
            this.emailError = response.message || 'This email is already registered. Please use a different email or log in.';
          }
        },
        (error) => {
          this.emailChecking = false;
          this.emailChecked = true;
          this.emailAvailable = false;
          this.emailError = 'Error checking email availability. Please try again.';
// $&
        }
      );
  }

  next(): void {
    if (this.step === 1 && this.step1.valid && this.tenantNameAvailable) {
      this.step++;
    } else if (this.step === 2 && this.step2.valid) {
      this.step++;
    } else if (this.step === 3 && this.step3.valid) {
      this.step++;
      // Initialize payment when moving to step 4
      this.initializePaystackPayment();
    }
  }

  selectPlan(plan: any) {
    this.selectedPlan = plan;
    this.isTrialMode = true; // Always start trial
    // Automatically start trial payment
    this.initializePaystackPayment();
  }

  // Initialize Paystack payment
  async initializePaystackPayment() {
    if (!this.selectedPlan) {
      return;
    }

    this.isLoading = true;
    this.paymentError = null;

    try {
      // Create customer in Paystack
      const customerData = {
        email: this.step2.get('adminEmail')?.value,
        first_name: this.step2.get('firstName')?.value,
        last_name: this.step2.get('lastName')?.value
      };

// $&

      const customerResponse = await this.http.post<any>(
        `${environment.apiUrl}/api/Subscription/paystack/create-customer`,
        customerData,
        { headers: { 'Content-Type': 'application/json' } }
      ).toPromise();

// $&

      if (customerResponse && customerResponse.status === true && customerResponse.data) {
        if (customerResponse.data.customer_code) {
          this.paystackCustomerCode = customerResponse.data.customer_code;
          await this.initializePaystackTransaction();
        } else if (customerResponse.data.id) {
          this.paystackCustomerCode = customerResponse.data.customer_code || '';
          await this.initializePaystackTransaction();
        } else {
          throw new Error('Invalid customer data received from Paystack');
        }
      } else {
        throw new Error(customerResponse?.message || 'Failed to create Paystack customer');
      }
    } catch (error: any) {
// $&
      this.paymentError = error?.error?.message || 'Failed to initialize Paystack payment';
      this.isLoading = false;
    }
  }

  // Complete registration after successful payment
  private async completeRegistration(paymentDetails: {
    paymentMethod: string;
    paymentIntentId: string;
    subscriptionId: string;
    customerId: string;
  }): Promise<void> {

    // Handle trial registration differently
    if (this.isTrialMode) {
      await this.completeTrialRegistration(paymentDetails.paymentIntentId);
      return;
    }
    const registrationData = {
      // Required fields for tenant creation
      tenantName: this.step1.get('tenantName')?.value,
      firstName: this.step2.get('firstName')?.value,
      lastName: this.step2.get('lastName')?.value,
      adminEmail: this.step2.get('adminEmail')?.value,

      // Payment and subscription fields
      paymentReference: paymentDetails.paymentIntentId,
      paymentIntentId: paymentDetails.paymentIntentId, // Keep for backward compatibility
      subscriptionId: this.selectedPlan?.subscriptionId || paymentDetails.subscriptionId, // Use plan's subscription ID if available
      customerId: this.paystackCustomerCode,
      paymentMethod: paymentDetails.paymentMethod.toLowerCase(),

      // Additional required fields
      amount: this.selectedPlan?.price || 0, // Ensure amount is always a number
      currency: 'ZAR',  // Explicitly set currency to ZAR

      // Optional fields
      planId: this.selectedPlan?.id,  // Include the plan ID if available
      planName: this.selectedPlan?.name || '',
      isNewRegistration: true
    };

    try {
      await this.http.post<any>(
        `${environment.apiUrl}/api/Tenant/create-database`,
        registrationData
      ).toPromise();

      this.registrationSuccess = true;
      this.errorMessage = '';
      localStorage.setItem('pendingOnboarding', JSON.stringify({
        tenantName: this.step1.get('tenantName')?.value,
        adminEmail: this.step2.get('adminEmail')?.value,
      }));
      setTimeout(() => {
        this.router.navigate(['/login']);
      }, 3000);
    } catch (error: any) {
      this.registrationSuccess = false;
      this.errorMessage = error.error?.message || 'An error occurred during registration.';
    } finally {
      this.isLoading = false;
      this.paymentProcessing = false;
    }
  }

  // Complete trial registration (R1 tokenization)
  private async completeTrialRegistration(paymentReference: string): Promise<void> {
    try {
      const selectedPlanCode: string | undefined = this.selectedPlan?.paystack_plan_code ? String(this.selectedPlan.paystack_plan_code) : undefined;
      if (!selectedPlanCode) {
        throw new Error('Selected plan does not have a valid Paystack plan code');
      }

      const trialData = {
        tenantName: this.step1.get('tenantName')?.value,
        firstName: this.step2.get('firstName')?.value,
        lastName: this.step2.get('lastName')?.value,
        adminEmail: this.step2.get('adminEmail')?.value,
        paymentReference: paymentReference,
        customerCode: this.paystackCustomerCode,
        selectedPlanCode: selectedPlanCode, // Always the real Paystack plan code (PLN_...)
        selectedPlanName: this.selectedPlan?.name || ''
      };

      await this.http.post<any>(
        `${environment.apiUrl}/api/Tenant/start-trial`,
        trialData
      ).toPromise();

      this.registrationSuccess = true;
      this.errorMessage = '';
      localStorage.setItem('pendingOnboarding', JSON.stringify({
        tenantName: this.step1.get('tenantName')?.value,
        adminEmail: this.step2.get('adminEmail')?.value,
      }));
      setTimeout(() => {
        this.router.navigate(['/login']);
      }, 3000);
    } catch (error: any) {
      this.registrationSuccess = false;
      this.errorMessage = error.error?.message || 'An error occurred during trial registration.';
    } finally {
      this.isLoading = false;
      this.paymentProcessing = false;
    }
  }

  // Initialize Paystack transaction
  async initializePaystackTransaction() {
    if (!this.selectedPlan || !this.paystackCustomerCode) {
// $&
      return;
    }

    this.isLoading = true;
    this.paymentError = null;

    try {
// $&

      if (!this.paystackPublicKey) {
        throw new Error('Paystack public key is not configured');
      }

      // Skip plan code validation for trial mode
      if (!this.isTrialMode && !this.selectedPlan.paystack_plan_code) {
        throw new Error('Selected plan does not have a valid Paystack plan code');
      }

      // Use admin email from step2 form
      const email = this.step2.get('adminEmail')?.value;
      if (!email) {
        throw new Error('Email address is required');
      }

      // For trial mode, charge R1 without a plan
      const paymentDetails: any = {
        email: email,
        amount: this.isTrialMode ? 100 : (this.selectedPlan.amount * 100), // R1 for trial, regular for subscription
        customer: this.paystackCustomerCode,
        metadata: {
          tenantName: this.step1.get('tenantName')?.value,
          isTrial: this.isTrialMode,
          custom_fields: [
            {
              display_name: 'Company Name',
              variable_name: 'company_name',
              value: this.step1.get('tenantName')?.value
            }
          ]
        }
      };

      // Only include plan for non-trial subscriptions
      if (!this.isTrialMode && this.selectedPlan.paystack_plan_code) {
        paymentDetails.plan = String(this.selectedPlan.paystack_plan_code);
      }
      const paystack = this.paymentService.getPaystackInstance();

      // Safety timeout: reset loading if Paystack popup never calls back
      // (blocked popup, failed checkout assets, or network error).
      let paymentTimeout: ReturnType<typeof setTimeout>;
      const armTimeout = () => {
        paymentTimeout = setTimeout(() => {
          if (this.isLoading) {
            this.paymentError =
              'The payment window did not respond. Your browser may have blocked ' +
              'the popup, or the payment service is unavailable. Please allow popups and try again.';
            this.isLoading = false;
          }
        }, 20_000);
      };

      const transactionConfig: any = {
        key: this.paystackPublicKey,
        email: paymentDetails.email,
        amount: paymentDetails.amount,
        customer: paymentDetails.customer,
        metadata: paymentDetails.metadata,
        onSuccess: async (transaction: any) => {
          clearTimeout(paymentTimeout);
          const subscriptionId = this.selectedPlan?.subscriptionId ||
                               transaction.subscription_id ||
                               transaction.reference;

          if (!this.isTrialMode && !subscriptionId) {
            this.paymentError = 'Could not determine subscription ID';
            this.isLoading = false;
            return;
          }

          await this.completeRegistration({
            paymentMethod: 'paystack',
            paymentIntentId: transaction.reference,
            subscriptionId: subscriptionId || 'TRIAL',
            customerId: transaction.customer?.customer_code || ''
          });
        },
        onCancel: () => {
          clearTimeout(paymentTimeout);
          this.paymentError = 'Payment was cancelled';
          this.isLoading = false;
        },
        onError: (error: any) => {
          clearTimeout(paymentTimeout);
          this.paymentError = error.message || 'Failed to process payment';
          this.isLoading = false;
        }
      };

      // Add plan only for non-trial transactions
      if (!this.isTrialMode && paymentDetails.plan) {
        transactionConfig.plan = paymentDetails.plan;
      }

      armTimeout();
      paystack.newTransaction(transactionConfig);
    } catch (error: any) {
// $&
      this.paymentError = error?.message || 'Failed to process payment';
      this.isLoading = false;
    }
  }

  ngOnDestroy() {
    // Any cleanup needed for Paystack can go here
  }

  // Navigate to previous step
  prev(): void {
    if (this.step > 1) {
      this.step--;
    }
  }

  // Process payment with Paystack
  async processPayment(): Promise<void> {
    if (this.paymentProcessing || !this.selectedPlan) {
      return;
    }

    this.paymentProcessing = true;
    this.paymentError = null;

    try {
      // Initialize Paystack payment
      const paystack = this.paymentService.getPaystackInstance();
      const paymentOptions = {
        key: environment.paystackPublicKey,
        email: this.step2.get('adminEmail')?.value,
        amount: this.selectedPlan.amount * 100, // Paystack expects amount in kobo
        currency: this.selectedPlan.currency || 'NGN',
        ref: `pay_${Date.now()}`,
        metadata: {
          tenantName: this.step1.get('tenantName')?.value,
          firstName: this.step2.get('firstName')?.value,
          lastName: this.step2.get('lastName')?.value
        },
        callback: (response: any) => {
          this.handlePaystackCallback(response);
        },
        onClose: () => {
          this.paymentProcessing = false;
          this.cdr.detectChanges();
        }
      };

      // @ts-ignore - Paystack types are not fully compatible
      paystack.newTransaction(paymentOptions);

      // Handle callback
      paystack.on('success', async (response: any) => {
        try {
          // Verify payment with backend
          const verificationResponse = await firstValueFrom(
            this.paymentService.verifyPaystackPayment(response.reference)
          );

          if (verificationResponse.status && verificationResponse.data.status === 'success') {
            // Complete registration
            await this.completeRegistration(response.reference);
          } else {
            this.paymentError = 'Payment verification failed. Please try again.';
          }
        } catch (error) {
// $&
          this.paymentError = 'Failed to verify payment. Please try again.';
        }
      });

      paystack.on('close', () => {
        this.paymentProcessing = false;
      });

      // Open Paystack payment modal
      paystack.open();
    } catch (error) {
// $&
      this.paymentError = 'An error occurred during payment processing. Please try again.';
      this.paymentProcessing = false;
    }
  }

  private async handlePaystackCallback(response: any) {
    try {
      // Verify payment with backend
      const verificationResponse = await firstValueFrom(
        this.paymentService.verifyPaystackPayment(response.reference)
      );

      if (verificationResponse.status && verificationResponse.data.status === 'success') {
        // Complete registration
        await this.completeRegistration(response.reference);
      } else {
        this.paymentError = 'Payment verification failed. Please try again.';
        this.paymentProcessing = false;
        this.cdr.detectChanges();
      }
    } catch (error) {
// $&
      this.paymentError = 'Failed to verify payment. Please try again.';
      this.paymentProcessing = false;
      this.cdr.detectChanges();
    }
  }
}
